import { isDefined } from "@clipboard-health/util-ts";
import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type UseQueryResult } from "@tanstack/react-query";
import { z } from "zod";

import { type QualificationHolidayFeeOverrides } from "../types";

const holidayFeeOverrideSchema = z
  .object({
    type: z.enum(["FLAT", "CHARGE_RATE_MULTIPLIER"]).optional(),
    increment: z.number().optional(),
  })
  .optional();

const facilityWithHolidayFeeOverridesSchema = z.object({
  qualificationHolidayFeeOverrides: z.record(z.string(), holidayFeeOverrideSchema).optional(),
});

const getFacilityHolidayFeeOverridesResponseSchema = z.array(facilityWithHolidayFeeOverridesSchema);

export type GetFacilityHolidayFeeOverridesResponse = z.infer<
  typeof getFacilityHolidayFeeOverridesResponseSchema
>;

const getFacilityHolidayFeeOverridesRequestSchema = z.object({
  facilityIds: z.string(),
  projection: z.string(),
});

export function useGetFacilityHolidayFeeOverrides(
  facilityId: string | undefined,
  options: UseGetQueryOptions<GetFacilityHolidayFeeOverridesResponse> = {}
): UseQueryResult<GetFacilityHolidayFeeOverridesResponse> & {
  overrides: QualificationHolidayFeeOverrides;
} {
  const result = useGetQuery({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/facilityprofile/details`,
    requestSchema: getFacilityHolidayFeeOverridesRequestSchema,
    responseSchema: getFacilityHolidayFeeOverridesResponseSchema,
    queryParams: {
      facilityIds: facilityId ?? "",
      projection: "qualificationHolidayFeeOverrides",
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_FACILITY_DETAILS_FAILURE,
    },
    ...options,
    enabled: options.enabled !== false && isDefined(facilityId),
  });

  const firstFacility = result.data?.[0];

  return {
    ...result,
    overrides: firstFacility?.qualificationHolidayFeeOverrides ?? {},
  };
}
